import React from 'react';
import { Box, Typography, Alert, Paper, IconButton, Tooltip, Divider, Skeleton, Menu, MenuItem, ListItemIcon, ListItemText, Button, Snackbar, TextField, Chip, CircularProgress, LinearProgress, useMediaQuery, useTheme } from '@mui/material';
import { AppDialog, ConfirmDialog } from '../components/dialog';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import GridLayout, { WidthProvider, Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { buildApiUrl, config } from '../config';
import ChartRenderer from '../components/ChartRenderer';
import EChartsRenderer from '../components/EChartsRenderer';
import { MetricCard } from '../components/MetricCard';
import { TableResultBlock } from '../components/TableResultBlock';

// Icon images are configurable via /public/icons/*.svg; replace files to change visuals
import { formatRelativeTime, formatAbsolute, parseBackendDate } from '../utils/date';
import IosShare from '@mui/icons-material/IosShare';
import Image from '@mui/icons-material/Image';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import Code from '@mui/icons-material/Code';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import html2pdf from 'html2pdf.js';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddIcon from '@mui/icons-material/Add';
import { getChartPalette } from '../theme/charts';
import { ModeContext } from '../theme/ThemeProvider';
import WidgetInsightsDrawer from '../components/WidgetInsightsDrawer';
import OnboardingCoachmark from '../components/OnboardingCoachmark';
import { SnackbarContext } from '../ui/SnackbarProvider';
import { useProductOnboarding } from '../hooks/useProductOnboarding';

const Grid = WidthProvider(GridLayout as any);

const ICONS = {
  refresh: '/icons/refresh.svg',
  fullscreen: '/icons/fullscreen.svg',
  more: '/icons/more.svg',
  drag: '/icons/drag.svg',
  edit: '/icons/edit.svg',
  delete: '/icons/delete.svg',
};

function IconImg({ src, alt }: { src: string; alt?: string }) {
  return <img src={src} alt={alt || ''} style={{ width: 18, height: 18, display: 'block' }} />;
}

type Widget = {
  widget_id: string;
  title: string;
  assistant_message_id: string;
  propose_call_id?: string | null;
  workflow_id?: string;  // Chat V2
  chart_event_id?: string;  // Chat V2
  pos_x: number;
  pos_y: number;
  width: number;
  height: number;
  created_at: string;
  rendered_at?: string | null;
  render_payload?: any;
};

export default function DashboardDetailPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNarrowCoach = useMediaQuery(theme.breakpoints.down('sm'));
  const { branding } = React.useContext(ModeContext);
  const { notify } = React.useContext(SnackbarContext);
  const { shouldShow: onboardingActive, currentStepId, loading: onboardingLoading } = useProductOnboarding();
  const chartPalette = React.useMemo(() => getChartPalette(branding), [branding]);
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const location = useLocation();
  
  // Try to get dashboard metadata from navigation state first (passed from listing page)
  const passedState = location.state as { dashboardName?: string; dashboardDescription?: string; dashboardId?: string } | null;
  const [dashboardName, setDashboardName] = React.useState<string>(passedState?.dashboardName || 'Dashboard');
  const [dashboardDescription, setDashboardDescription] = React.useState<string | null>(passedState?.dashboardDescription || null);
  const [widgets, setWidgets] = React.useState<Widget[]>([]);
  const [specs, setSpecs] = React.useState<Record<string, any>>({});
  const [widgetErrors, setWidgetErrors] = React.useState<Record<string, string | null>>({});
  const [widgetLoading, setWidgetLoading] = React.useState<Record<string, boolean>>({});
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [fullscreenId, setFullscreenId] = React.useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuForId, setMenuForId] = React.useState<string | null>(null);
  const [subAnchor, setSubAnchor] = React.useState<null | HTMLElement>(null);
  // Anchor for the standalone Download menu rendered in the fullscreen
  // dialog header. Kept separate from `subAnchor` so the per-widget kebab
  // and the fullscreen header don't fight over the same Menu instance.
  const [fullscreenDlAnchor, setFullscreenDlAnchor] = React.useState<null | HTMLElement>(null);
  const [resizing, setResizing] = React.useState<{ id: string; w: number; h: number } | null>(null);
  const [refreshedAt, setRefreshedAt] = React.useState<Record<string, string>>({});
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewHtml, setPreviewHtml] = React.useState<string>('');
  const [exporting, setExporting] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editForId, setEditForId] = React.useState<string | null>(null);
  const [editTitle, setEditTitle] = React.useState<string>('');
  const [savingTitle, setSavingTitle] = React.useState(false);
  const [inlineEditId, setInlineEditId] = React.useState<string | null>(null);
  const [inlineEditValue, setInlineEditValue] = React.useState<string>('');
  const [addWidgetOpen, setAddWidgetOpen] = React.useState(false);
  const [addWidgetQuestion, setAddWidgetQuestion] = React.useState('');
  const navigate = useNavigate();
  const gridRef = React.useRef<HTMLDivElement | null>(null);
  const [insightsOpen, setInsightsOpen] = React.useState(false);
  const [insightsFor, setInsightsFor] = React.useState<Widget | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteWidgetId, setDeleteWidgetId] = React.useState<string | null>(null);
  const chartViewRefs = React.useRef<Record<string, React.RefObject<any>>>({});
  const [dashboardLastUpdated, setDashboardLastUpdated] = React.useState<string | null>(null);
  const [isRefreshingAll, setIsRefreshingAll] = React.useState(false);
  const [refreshAllProgress, setRefreshAllProgress] = React.useState({ done: 0, total: 0, succeeded: 0, failed: 0 });
  const refreshAllCancelRef = React.useRef(false);
  const [showEditNudge, setShowEditNudge] = React.useState(false);
  const [insightsOnboardingMode, setInsightsOnboardingMode] = React.useState(false);
  const editAgentBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const closePreviewDialog = React.useCallback(() => {
    setPreviewOpen(false);
    try { window.dispatchEvent(new Event('chat:export:restore')); } catch {}
  }, []);
  const closeEditDialog = React.useCallback(() => { setEditOpen(false); setEditForId(null); setEditTitle(''); }, []);
  const closeAddWidgetDialog = React.useCallback(() => { setAddWidgetOpen(false); setAddWidgetQuestion(''); }, []);
  const closeDeleteConfirmDialog = React.useCallback(() => { setDeleteConfirmOpen(false); setDeleteWidgetId(null); }, []);

  const tenantId = React.useMemo(() => {
    try { const t = JSON.parse(String(window.localStorage.getItem('klaris_tenant'))); return String(t?.tenant_id || ''); } catch { return ''; }
  }, []);

  // Helper to format snake_case titles to Title Case
  const toTitleCase = React.useCallback((str: string): string => {
    // If already has spaces or uppercase letters, return as-is (user customized)
    if (str.includes(' ') || /[A-Z]/.test(str)) {
      return str;
    }
    // Otherwise format snake_case
    return str
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, []);

  // Helper to get widget title - format snake_case titles
  const getWidgetDisplayTitle = React.useCallback((widget: Widget): string => {
    return toTitleCase(widget.title);
  }, [toTitleCase]);

  const startInlineEdit = React.useCallback((w: Widget) => {
    setInlineEditId(w.widget_id);
    setInlineEditValue(toTitleCase(w.title));
  }, [toTitleCase]);

  const saveInlineTitle = React.useCallback(async (widgetId: string) => {
    if (!tenantId || !dashboardId || !inlineEditValue.trim()) {
      setInlineEditId(null);
      return;
    }
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(widgetId)}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ title: inlineEditValue.trim() })
        } as RequestInit
      );
      if (resp.ok) {
        setWidgets(prev => prev.map(w =>
          w.widget_id === widgetId ? { ...w, title: inlineEditValue.trim() } : w
        ));
      }
    } catch {}
    setInlineEditId(null);
  }, [tenantId, dashboardId, inlineEditValue]);

  // Fetch individual widget data from database (no re-execution)
  // Falls back to synchronous /render if render_payload is not yet available
  // (background task may still be running after widget creation)
  const fetchWidgetData = React.useCallback(async (widgetId: string) => {
    if (!tenantId || !dashboardId) return;
    const token = window.localStorage.getItem('klaris_jwt');
    setWidgetLoading(prev => ({ ...prev, [widgetId]: true }));
    setWidgetErrors(prev => ({ ...prev, [widgetId]: null }));
    
    try {
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(String(widgetId))}`),
        { headers: { Authorization: `Bearer ${token}` } } as RequestInit
      );
      const data = await resp.json();
      
      if (!resp.ok) {
        throw new Error(data?.detail || data?.error || 'Failed to load widget');
      }
      
      // Extract render_payload from widget response
      const renderPayload = data?.render_payload;
      if (renderPayload && (renderPayload.spec || renderPayload.type)) {
        setSpecs(prev => ({ ...prev, [widgetId]: renderPayload }));
        if (data.rendered_at) {
          setRefreshedAt(prev => ({ ...prev, [widgetId]: data.rendered_at }));
        }
      } else {
        // render_payload not yet available (background task may still be running)
        // Fall back to synchronous /render endpoint
        const renderResp = await fetch(
          buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(String(widgetId))}/render`),
          { headers: { Authorization: `Bearer ${token}` } } as RequestInit
        );
        const renderData = await renderResp.json();
        
        if (!renderResp.ok) {
          throw new Error(renderData?.detail || renderData?.error || 'Failed to render widget');
        }
        
        if (renderData?.spec || renderData?.type) {
          setSpecs(prev => ({ ...prev, [widgetId]: renderData }));
          // Don't set refreshedAt here - this is a non-force render (initial load fallback),
          // not an actual data refresh. rendered_at from the server is the source of truth.
        }
      }
    } catch (e: any) {
      setWidgetErrors(prev => ({ ...prev, [widgetId]: e?.message || 'Failed to load widget' }));
    } finally {
      setWidgetLoading(prev => ({ ...prev, [widgetId]: false }));
    }
  }, [tenantId, dashboardId]);

  // Refresh widget by re-executing (calls /render endpoint)
  const fetchWidget = React.useCallback(async (w: Widget) => {
    const token = window.localStorage.getItem('klaris_jwt');
    setWidgetLoading(prev => ({ ...prev, [w.widget_id]: true }));
    setWidgetErrors(prev => ({ ...prev, [w.widget_id]: null }));
    
    try {
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(String(w.widget_id))}/render?force=true`),
        { headers: { Authorization: `Bearer ${token}` } } as RequestInit
      );
      const data = await resp.json();
      
      if (!resp.ok) {
        throw new Error(data?.detail || data?.error || 'Failed to refresh widget');
      }
      
      // Store entire response for all widget types
      if (data?.spec || data?.type) {
        setSpecs(prev => ({ ...prev, [w.widget_id]: data }));
        // Re-fetch widget to get the real server-side rendered_at
        // (only set by backend when replay actually re-executed queries)
        try {
          const wResp = await fetch(
            buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(String(w.widget_id))}`),
            { headers: { Authorization: `Bearer ${token}` } } as RequestInit
          );
          const wData = await wResp.json();
          if (wResp.ok && wData?.rendered_at) {
            setRefreshedAt(prev => ({ ...prev, [w.widget_id]: wData.rendered_at }));
            setDashboardLastUpdated(wData.rendered_at);
            setWidgets(prev => prev.map(ww =>
              ww.widget_id === w.widget_id ? { ...ww, render_payload: data, rendered_at: wData.rendered_at } : ww
            ));
          } else {
            setWidgets(prev => prev.map(ww =>
              ww.widget_id === w.widget_id ? { ...ww, render_payload: data } : ww
            ));
          }
        } catch {
          // If re-fetch fails, just update the render_payload without touching rendered_at
          setWidgets(prev => prev.map(ww =>
            ww.widget_id === w.widget_id ? { ...ww, render_payload: data } : ww
          ));
        }
      }
    } catch (e: any) {
      setWidgetErrors(prev => ({ ...prev, [w.widget_id]: e?.message || 'Failed to refresh widget' }));
    } finally {
      setWidgetLoading(prev => ({ ...prev, [w.widget_id]: false }));
    }
  }, [tenantId, dashboardId]);

  const deleteWidget = React.useCallback(async (id: string) => {
    if (!tenantId || !dashboardId) return;
    const token = window.localStorage.getItem('klaris_jwt');
    try {
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(id)}`), {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      } as RequestInit);
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j?.detail || j?.error || 'Failed to delete');
      }
      setWidgets(prev => prev.filter(w => w.widget_id !== id));
      setSpecs(prev => { const copy = { ...prev }; delete (copy as any)[id]; return copy; });
      setWidgetErrors(prev => { const copy = { ...prev }; delete (copy as any)[id]; return copy; });
    } catch (e) {
      // Surface an alert at top-level for now
      setError((e as any)?.message || 'Failed to delete widget');
    } finally {
      setMenuAnchor(null); setMenuForId(null);
    }
  }, [tenantId, dashboardId]);

  const refreshAllWidgets = React.useCallback(async () => {
    if (!tenantId || !dashboardId || widgets.length === 0) return;
    const token = window.localStorage.getItem('klaris_jwt');
    refreshAllCancelRef.current = false;
    setIsRefreshingAll(true);
    setRefreshAllProgress({ done: 0, total: widgets.length, succeeded: 0, failed: 0 });

    const CONCURRENCY = 3;
    let succeeded = 0;
    let failed = 0;
    let done = 0;
    const queue = [...widgets];

    const processOne = async () => {
      while (queue.length > 0) {
        if (refreshAllCancelRef.current) return;
        const w = queue.shift()!;
        setWidgetLoading(prev => ({ ...prev, [w.widget_id]: true }));
        setWidgetErrors(prev => ({ ...prev, [w.widget_id]: null }));
        try {
          const resp = await fetch(
            buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(String(w.widget_id))}/render?force=true`),
            { headers: { Authorization: `Bearer ${token}` } } as RequestInit
          );
          const data = await resp.json();
          if (!resp.ok) throw new Error(data?.detail || data?.error || 'Failed to refresh');
          if (data?.spec || data?.type) {
            setSpecs(prev => ({ ...prev, [w.widget_id]: data }));
            // Re-fetch widget to get real server-side rendered_at
            try {
              const wResp = await fetch(
                buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(String(w.widget_id))}`),
                { headers: { Authorization: `Bearer ${token}` } } as RequestInit
              );
              const wData = await wResp.json();
              if (wResp.ok && wData?.rendered_at) {
                setRefreshedAt(prev => ({ ...prev, [w.widget_id]: wData.rendered_at }));
                setWidgets(prev => prev.map(ww =>
                  ww.widget_id === w.widget_id ? { ...ww, render_payload: data, rendered_at: wData.rendered_at } : ww
                ));
              } else {
                setWidgets(prev => prev.map(ww =>
                  ww.widget_id === w.widget_id ? { ...ww, render_payload: data } : ww
                ));
              }
            } catch {
              setWidgets(prev => prev.map(ww =>
                ww.widget_id === w.widget_id ? { ...ww, render_payload: data } : ww
              ));
            }
          }
          succeeded++;
        } catch (e: any) {
          setWidgetErrors(prev => ({ ...prev, [w.widget_id]: e?.message || 'Refresh failed' }));
          failed++;
        } finally {
          setWidgetLoading(prev => ({ ...prev, [w.widget_id]: false }));
          done++;
          setRefreshAllProgress({ done, total: widgets.length, succeeded, failed });
        }
      }
    };

    const workers = Array.from({ length: Math.min(CONCURRENCY, widgets.length) }, () => processOne());
    await Promise.all(workers);

    setIsRefreshingAll(false);
    const now = new Date().toISOString();
    setDashboardLastUpdated(now);
    if (refreshAllCancelRef.current) {
      notify(`Refresh cancelled - ${succeeded} updated, ${failed} failed`, 'info');
    } else if (failed > 0) {
      notify(`Refreshed ${succeeded}/${widgets.length} widgets (${failed} failed)`, 'warning');
    } else {
      notify(`All ${succeeded} widgets refreshed successfully`, 'success');
    }
  }, [tenantId, dashboardId, widgets, notify]);

  const cancelRefreshAll = React.useCallback(() => {
    refreshAllCancelRef.current = true;
  }, []);

  React.useEffect(() => {
    if (!tenantId || !dashboardId) return;
    (async () => {
      setLoading(true); setError(null);
      try {
        const token = window.localStorage.getItem('klaris_jwt');
        // Fetch dashboard metadata and widget list (without render_payload)
        const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets`), {
          headers: { Authorization: `Bearer ${token}` }
        } as RequestInit);
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.detail || data?.error || 'Failed to load widgets');
        
        // Extract dashboard metadata from response (only if not already passed via route state)
        if (data?.dashboard && !passedState?.dashboardName) {
          if (data.dashboard.name) setDashboardName(String(data.dashboard.name));
          setDashboardDescription(data.dashboard.description || null);
        }
        
        const list: Widget[] = Array.isArray(data?.widgets) ? data.widgets : [];
        // Order widgets so newly added (without saved positions) appear last.
        // 1) Keep items with saved positions first (visual layout already defined)
        // 2) Append items without positions, sorted by created_at ascending (newest last)
        const positioned = list.filter((w: any) => Number.isFinite(w?.pos_x) && Number.isFinite(w?.pos_y));
        const unpositioned = list.filter((w: any) => !Number.isFinite(w?.pos_x) || !Number.isFinite(w?.pos_y));
        unpositioned.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        const ordered = [...positioned, ...unpositioned];
        setWidgets(ordered);
        
        // Compute dashboard-level last updated from the most recent widget
        // rendered_at. Use parseBackendDate (not the bare `new Date()`)
        // because FastAPI emits naive ISO strings without a `Z` suffix —
        // ES2015 then interprets them as *local* time, which silently shifts
        // the value by the viewer's UTC offset (this surfaced as
        // "Updated 6 hours ago" for IST users on a freshly rendered board).
        const renderedAts = list
          .map((w: any) => w.rendered_at)
          .filter(Boolean)
          .map((d: string) => parseBackendDate(d)?.getTime())
          .filter((t: number | undefined): t is number => typeof t === 'number' && !isNaN(t));
        if (renderedAts.length > 0) {
          setDashboardLastUpdated(new Date(Math.max(...renderedAts)).toISOString());
        }
        
        // Initialize loading states for all widgets
        const initialLoading: Record<string, boolean> = {};
        const initialErrors: Record<string, string | null> = {};
        for (const w of list) {
          initialLoading[w.widget_id] = true; // Start with loading state
          initialErrors[w.widget_id] = null;
        }
        setWidgetLoading(initialLoading);
        setWidgetErrors(initialErrors);
        setSpecs({});
        setRefreshedAt({});
        
        // Fetch each widget's render data individually for progressive loading
        // This allows fast widgets to render immediately while slow ones show shimmer
        for (const w of list) {
          fetchWidgetData(w.widget_id);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, dashboardId, fetchWidgetData]);

  const layout = React.useMemo(() => (
    widgets.map((w, idx) => {
      const defaultX = (idx * 4) % 12;
      const defaultY = Math.floor((idx * 4) / 12) * 3;
      const x = (w.pos_x ?? defaultX);
      const y = (w.pos_y ?? defaultY);
      const width = (w.width ?? 4);
      const height = (w.height ?? 3);
      return { i: w.widget_id, x, y, w: width, h: height, resizeHandles: ['se'] } as any;
    })
  ), [widgets]);

  /** Single-column stack for narrow viewports — preserves reading order from saved grid positions */
  const mobileStackLayout = React.useMemo(() => {
    const base = widgets.map((w, idx) => {
      const defaultX = (idx * 4) % 12;
      const defaultY = Math.floor((idx * 4) / 12) * 3;
      const x = (w.pos_x ?? defaultX);
      const y = (w.pos_y ?? defaultY);
      const width = (w.width ?? 4);
      const height = (w.height ?? 3);
      return { i: w.widget_id, x, y, w: width, h: height };
    }).sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
    let yAcc = 0;
    return base.map((l) => {
      const item = { i: l.i, x: 0, y: yAcc, w: 1, h: l.h } as Layout;
      yAcc += l.h;
      return item;
    });
  }, [widgets]);

  const gridLayout = isMobile ? mobileStackLayout : layout;

  const saveTimer = React.useRef<number | null>(null);
  const onLayoutChange = React.useCallback((current: Layout[]) => {
    try {
      if (!tenantId || !dashboardId) return;
      const token = window.localStorage.getItem('klaris_jwt');
      const body = { items: current.map(l => ({ widget_id: String(l.i), x: l.x, y: l.y, w: l.w, h: l.h })) };
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(async () => {
        await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/layout`), {
          method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body)
        } as RequestInit);
      }, 400);
    } catch {}
  }, [tenantId, dashboardId]);

  const onLayoutChangeSafe = React.useCallback(
    (current: Layout[]) => {
      if (!isMobile) onLayoutChange(current);
    },
    [isMobile, onLayoutChange],
  );

  const openMenu = (e: React.MouseEvent<HTMLElement>, id: string) => { setMenuAnchor(e.currentTarget); setMenuForId(id); };
  const closeMenu = () => { setMenuAnchor(null); setMenuForId(null); setSubAnchor(null); };
  
  const handleDeleteClick = React.useCallback(() => {
    if (menuForId) {
      setDeleteWidgetId(menuForId);
      setDeleteConfirmOpen(true);
      closeMenu();
    }
  }, [menuForId, closeMenu]);
  
  const confirmDelete = React.useCallback(async () => {
    if (deleteWidgetId) {
      await deleteWidget(deleteWidgetId);
      closeDeleteConfirmDialog();
    }
  }, [deleteWidgetId, deleteWidget, closeDeleteConfirmDialog]);
  const openEdit = () => {
    if (menuForId) {
      setEditForId(menuForId);
      const w = widgets.find(x => x.widget_id === menuForId);
      // Show titleized version in edit dialog
      setEditTitle(w?.title ? toTitleCase(w.title) : '');
      setEditOpen(true);
    }
    closeMenu();
  };

  const saveWidgetTitle = React.useCallback(async () => {
    if (!editForId || !tenantId || !dashboardId) return;
    
    setSavingTitle(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(editForId)}`),
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ title: editTitle })
        } as RequestInit
      );
      
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.detail || data?.error || 'Failed to update title');
      }
      
      // Update local widget state
      setWidgets(prev => prev.map(w => 
        w.widget_id === editForId ? { ...w, title: editTitle } : w
      ));
      
      notify('Widget title updated successfully', 'success');
      closeEditDialog();
    } catch (e: any) {
      setError(e?.message || 'Failed to update widget title');
    } finally {
      setSavingTitle(false);
    }
  }, [editForId, editTitle, tenantId, dashboardId, closeEditDialog]);

  const openFullscreen = (id: string) => setFullscreenId(id);
  const closeFullscreen = () => {
    setFullscreenId(null);
    setFullscreenDlAnchor(null);
  };

  // Single chart-download helper that picks the right code path based on
  // widget type. ECharts widgets expose `getDataURL` on the chart instance;
  // legacy Vega-Lite widgets expose `toCanvas` / `toSVG` on the view. We
  // intentionally read the renderer instance from `chartViewRefs.current[id]`
  // at call time (not render time) so we don't depend on React re-rendering
  // the consumer after `onChartReady` mutates the ref.
  const downloadWidgetAs = React.useCallback(
    async (id: string, format: 'png' | 'jpeg' | 'svg') => {
      const ref = chartViewRefs.current[id];
      const inst = ref && ref.current;
      if (!inst) return;
      const isECharts = specs[id]?.type === 'echarts';
      try {
        if (isECharts) {
          // ECharts canvas-renderer instances support png/jpeg natively.
          // Passing 'svg' here falls back to PNG for canvas-rendered charts,
          // which is acceptable until we move ECharts to the SVG renderer.
          const url = inst.getDataURL({
            type: format === 'svg' ? 'svg' : format,
            pixelRatio: Math.max((window.devicePixelRatio || 1) * 2, 4),
            backgroundColor: '#ffffff',
          });
          if (!url) return;
          const a = document.createElement('a');
          a.href = url;
          a.download = `chart-${Date.now()}.${format}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }
        if (format === 'svg') {
          const svg = await inst.toSVG();
          const url = `data:image/svg+xml;base64,${btoa(svg)}`;
          const a = document.createElement('a');
          a.href = url;
          a.download = `chart-${Date.now()}.svg`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }
        const dpr = window.devicePixelRatio || 1;
        const scaleFactor = Math.max(4, dpr * 2);
        const canvas = await inst.toCanvas(scaleFactor);
        const c2 = document.createElement('canvas');
        c2.width = canvas.width;
        c2.height = canvas.height;
        const ctx = c2.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, c2.width, c2.height);
          ctx.drawImage(canvas, 0, 0);
        }
        const url = c2.toDataURL(`image/${format}`, format === 'jpeg' ? 0.95 : 1.0);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chart-${Date.now()}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (err) {
        console.error('Failed to download chart:', err);
      }
    },
    [specs],
  );

  async function openDashboardPreview() {
    try {
      const node = gridRef.current;
      if (!node) return;

      // Find all widget cards and sort by visual top/left
      const cards = Array.from(node.querySelectorAll('.widget-card')) as HTMLElement[];
      const withPos = cards.map((el) => {
        const r = el.getBoundingClientRect();
        return { el, top: Math.round(r.top), left: Math.round(r.left) };
      }).sort((a, b) => (a.top - b.top) || (a.left - b.left));

      // Group into rows by top position tolerance
      const rows: HTMLElement[][] = [];
      const tol = 8; // px tolerance for same row
      let currentTop: number | null = null;
      withPos.forEach(({ el, top }) => {
        if (currentTop === null || Math.abs(top - currentTop) > tol) {
          rows.push([el]);
          currentTop = top;
        } else {
          rows[rows.length - 1].push(el);
        }
      });

      // Build HTML: header + each row as a flex wrapper
      const parts: string[] = [];
      const escapeHtml = (s: string) => String(s || '').replace(/[&<>"']/g, (ch) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#39;'} as any)[ch] || ch);
      const header = `
        <div class="export-root" style="font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial;">
          <style>
            .row-wrapper { display: flex; gap: 12px; width: 100%; page-break-inside: avoid; break-inside: avoid; margin-bottom: 12px; }
            .row-wrapper > .widget-card { flex: 1 1 0; min-width: 0; page-break-inside: avoid; break-inside: avoid; }
            .export-root .layout { overflow: visible !important; }
            img { image-rendering: -webkit-optimize-contrast; }
          </style>
          <div style="font-weight:700; font-size: 18px; text-align:center;">${escapeHtml(dashboardName)}</div>
          <div style="height:2px; background:#000; margin:6px 0 10px 0;"></div>
        </div>
      `;
      parts.push(header);

      rows.forEach((row) => {
        const rowWrapper = document.createElement('div');
        rowWrapper.className = 'row-wrapper';
        row.forEach((orig) => {
          const clone = orig.cloneNode(true) as HTMLElement;
          // Inline canvases within this widget
          const liveCanvases = orig.querySelectorAll('canvas');
          const cloneCanvases = clone.querySelectorAll('canvas');
          const len = Math.min(liveCanvases.length, cloneCanvases.length);
          for (let i = 0; i < len; i++) {
            try {
              const live = liveCanvases[i] as HTMLCanvasElement;
              const url = live.toDataURL('image/png');
              const img = document.createElement('img');
              img.src = url;
              (img.style as any).width = '100%';
              (img.style as any).height = 'auto';
              const c = cloneCanvases[i];
              c.parentNode && c.parentNode.replaceChild(img, c);
            } catch {}
          }
          rowWrapper.appendChild(clone);
        });
        parts.push(rowWrapper.outerHTML);
      });

      setPreviewHtml(parts.join(''));
      setPreviewOpen(true);
    } finally {
      // No-op: we avoided export mode; live charts remain intact
    }
  }

  async function exportDashboardPdf() {
    if (!previewHtml) return;
    setExporting(true);
    try {
      const container = document.createElement('div');
      container.innerHTML = previewHtml;
      const ts = new Date();
      const y = ts.getFullYear();
      const m = String(ts.getMonth() + 1).padStart(2, '0');
      const d = String(ts.getDate()).padStart(2, '0');
      const hh = String(ts.getHours()).padStart(2, '0');
      const mm = String(ts.getMinutes()).padStart(2, '0');
      const brandSlug = (branding?.name || config.brandName).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const filename = `${brandSlug}-dashboard-${y}${m}${d}-${hh}${mm}.pdf`;
      const opt: any = {
        margin: 10,
        filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      };
      await (html2pdf() as any).set(opt).from(container).save();
    } finally {
      setExporting(false);
    }
  }

  // Listen for export requests from header (DashboardLayout)
  React.useEffect(() => {
    const onExport = () => { try { openDashboardPreview(); } catch {} };
    window.addEventListener('dashboard:export', onExport as any);
    return () => window.removeEventListener('dashboard:export', onExport as any);
  }, [openDashboardPreview]);
  
  // Listen for dashboard rename events from breadcrumb
  React.useEffect(() => {
    const onRenamed = (ev: Event) => { 
      try { 
        const detail = (ev as CustomEvent).detail;
        if (detail?.name) {
          setDashboardName(detail.name);
        }
      } catch {} 
    };
    window.addEventListener('dashboard:renamed', onRenamed as any);
    return () => window.removeEventListener('dashboard:renamed', onRenamed as any);
  }, []);

  // “Chat with Copilot” coach mark: only during onboarding pin-widget flow (not every add).
  // After pin, backend marks widget_added done so current_step_id is usually widget_summarized next.
  React.useEffect(() => {
    if (onboardingLoading) return;
    try {
      const flagged = localStorage.getItem('klaris_nudge_edit_agent') === '1';
      const inPinWidgetFlow =
        currentStepId === 'widget_added' || currentStepId === 'widget_summarized';
      const showNudge = flagged && onboardingActive && inPinWidgetFlow;
      setShowEditNudge(showNudge);
      if (flagged && !showNudge) {
        localStorage.removeItem('klaris_nudge_edit_agent');
      }
    } catch {
      setShowEditNudge(false);
    }
  }, [onboardingLoading, onboardingActive, currentStepId]);

  const onResize = React.useCallback((_layout: any, _oldItem: any, newItem: any) => {
    try { setResizing({ id: String(newItem.i), w: Number(newItem.w), h: Number(newItem.h) }); } catch {}
  }, []);
  const onResizeStop = React.useCallback(() => { setResizing(null); }, []);

  return (
    <Box sx={{
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      minWidth: 0,
      bgcolor: (theme) => theme.palette.mode === 'light' ? '#F8F9FA' : 'background.default',
      minHeight: '100%',
    }}>
      {/* Page Header - clean toolbar with bottom border */}
      {!loading && (
        <Box
          sx={{
            mb: 2,
            pb: 1.5,
            borderBottom: (theme: any) => `1px solid ${theme.tokens.border.subtle}`,
          }}
        >
          {/* Description (only if present) */}
          {dashboardDescription && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: '0.8125rem', lineHeight: 1.4 }}
            >
              {dashboardDescription}
            </Typography>
          )}

          {/* Toolbar row */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1.25, sm: 2 },
              minHeight: { sm: 36 },
            }}
          >
            {/* Left: metadata */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: { xs: 0.75, sm: 1.5 },
                rowGap: 0.5,
                minWidth: 0,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '0.8125rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.75 }}
              >
                <DashboardIcon sx={{ fontSize: 16, opacity: 0.6, flexShrink: 0 }} />
                {widgets.length} widget{widgets.length !== 1 ? 's' : ''}
              </Typography>
              {dashboardLastUpdated && (
                <>
                  <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.75rem', display: { xs: 'none', sm: 'block' } }}>·</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                      fontWeight: 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 14, opacity: 0.5, flexShrink: 0 }} />
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
                      Updated {formatRelativeTime(dashboardLastUpdated)}
                    </Box>
                  </Typography>
                </>
              )}
            </Box>

            {/* Right: actions */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {isRefreshingAll ? (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={cancelRefreshAll}
                  sx={{
                    borderRadius: '7px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    height: 32,
                    px: 1.5,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    flex: { xs: 1, sm: 'none' },
                  }}
                >
                  Cancel
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon sx={{ fontSize: '16px !important' }} />}
                  onClick={refreshAllWidgets}
                  disabled={widgets.length === 0}
                  sx={{
                    borderRadius: '7px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    height: 32,
                    px: 1.5,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    flex: { xs: 1, sm: 'none' },
                    minWidth: { xs: 0, sm: undefined },
                  }}
                >
                  {isMobile ? 'Refresh' : 'Refresh All'}
                </Button>
              )}

              <Button
                variant="outlined"
                size="small"
                startIcon={<IosShare sx={{ fontSize: '16px !important' }} />}
                onClick={openDashboardPreview}
                sx={{
                  borderRadius: '7px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  height: 32,
                  px: 1.5,
                  borderColor: 'divider',
                  color: 'text.secondary',
                  flex: { xs: 1, sm: 'none' },
                  minWidth: { xs: 0, sm: undefined },
                }}
              >
                Export
              </Button>

              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddWidgetOpen(true)}
                sx={{
                  borderRadius: '7px',
                  textTransform: 'none',
                  fontWeight: 600,
                  height: 32,
                  fontSize: '0.8125rem',
                  boxShadow: 'none',
                  flex: { xs: '1 1 100%', sm: 'none' },
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                {isMobile ? 'Add' : 'Add Widget'}
              </Button>
            </Box>
          </Box>

          {/* Refresh progress - slim bar below toolbar */}
          {isRefreshingAll && (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={14} thickness={5} />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem', color: 'text.secondary' }}>
                    Refreshing {refreshAllProgress.done} of {refreshAllProgress.total}
                  </Typography>
                  {refreshAllProgress.failed > 0 && (
                    <Typography variant="caption" color="error.main" sx={{ fontWeight: 500, fontSize: '0.6875rem' }}>
                      {refreshAllProgress.failed} failed
                    </Typography>
                  )}
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={refreshAllProgress.total > 0 ? (refreshAllProgress.done / refreshAllProgress.total) * 100 : 0}
                  sx={{ height: 3, borderRadius: 2, bgcolor: 'action.hover' }}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
      
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      
      {/* Loading Skeleton for entire dashboard */}
      {loading && (
        <Box>
          {/* Actions bar skeleton */}
          <Box 
            sx={(t) => ({ 
              mb: 2.5, 
              p: 1.5,
              borderRadius: `${t.ds.radius.md}px`,
              bgcolor: t.tokens.surface.sunken,
              border: `1px solid ${t.tokens.border.subtle}`,
            })}
          >
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: '8px' }} />
              <Skeleton variant="rectangular" width={180} height={36} sx={{ borderRadius: '8px' }} />
            </Box>
          </Box>
          {/* Widget skeletons */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(320px, 1fr))' }, gap: 2 }}>
            {[1, 2, 3].map((i) => (
              <Paper 
                key={i}
                sx={(t) => ({ 
                  p: 0, 
                  borderRadius: `${t.ds.radius.md}px`,
                  height: 300,
                  border: `1px solid ${t.tokens.border.subtle}`,
                  bgcolor: t.tokens.surface.raised,
                })}
              >
                <Box sx={(t) => ({ p: 1.5, borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="40%" height={14} sx={{ mt: 0.25 }} />
                </Box>
                <Box sx={{ p: 1.5 }}>
                  <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: '6px' }} />
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      )}
      
      
      <Box ref={gridRef} sx={{ ml: { xs: 0, md: '-10px' }, width: '100%', minWidth: 0 }}>
      {(!loading && widgets.length === 0) ? (
        <Paper 
          variant="outlined" 
          sx={(t) => ({ 
            p: 4, 
            textAlign: 'center',
            borderRadius: `${t.ds.radius.md}px`,
            border: `2px dashed ${t.tokens.border.default}`,
            bgcolor: t.tokens.surface.sunken,
            boxShadow: 'none'
          })}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 1, 
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '1.125rem'
            }}
          >
            No widgets yet
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ maxWidth: 400, mx: 'auto', fontSize: '0.875rem' }}
          >
            Add a widget from a chat answer using "Add to Dashboard" to start building your dashboard.
          </Typography>
        </Paper>
      ) : (
        <Grid
          className="layout"
          layout={gridLayout as any}
          rowHeight={isMobile ? 68 : 80}
          cols={isMobile ? 1 : 12}
          onLayoutChange={onLayoutChangeSafe as any}
          isDraggable={!isMobile}
          isResizable={!isMobile}
          draggableHandle=".widget-drag-handle"
          onResize={onResize as any}
          onResizeStop={onResizeStop as any}
          measureBeforeMount
          useCSSTransforms
          compactType="vertical"
          margin={isMobile ? [8, 8] : [14, 14]}
        >
          {widgets.map((w) => (
            <div key={w.widget_id}>
              <Paper 
                className="widget-card" 
                variant="outlined" 
                sx={(t) => ({ 
                  p: 0, 
                  borderRadius: `${t.ds.radius.md}px`,
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  overflow: 'hidden', 
                  position: 'relative',
                  border: `1px solid ${t.tokens.border.subtle}`,
                  bgcolor: t.tokens.surface.raised,
                  boxShadow: t.tokens.shadow.sm,
                  transition: `box-shadow ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}, transform ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, border-color ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}`,
                  '& .widget-actions': {
                    opacity: 0,
                    transition: `opacity ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                  },
                  '&:hover .widget-actions': {
                    opacity: 1,
                  },
                  '&:hover': {
                    boxShadow: t.tokens.shadow.md,
                    borderColor: t.tokens.border.default,
                  },
                })}
              >
              {resizing?.id === w.widget_id && (
                <Box sx={(t) => ({ position: 'absolute', top: 6, right: 6, bgcolor: t.tokens.surface.inverse, color: t.tokens.text.inverse, px: 0.75, py: 0.375, borderRadius: `${t.ds.radius.sm}px`, fontSize: '0.75rem', fontWeight: 600, zIndex: 2, boxShadow: t.tokens.shadow.md })}>
                  {resizing.w}×{resizing.h}
                </Box>
              )}

              {/* Full-card shimmer skeleton — covers header + content on every load/refresh */}
              {widgetLoading[w.widget_id] && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 20,
                    borderRadius: 'inherit',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: (theme) => theme.palette.mode === 'light'
                      ? 'background.paper'
                      : 'background.default',
                    // shimmer wave
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background: (theme) => theme.palette.mode === 'light'
                        ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.75) 50%, transparent 100%)'
                        : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)',
                      backgroundSize: '200% 100%',
                      animation: 'widgetShimmer 1.4s ease-in-out infinite',
                    },
                    '@keyframes widgetShimmer': {
                      '0%':   { backgroundPosition: '-200% 0' },
                      '100%': { backgroundPosition:  '200% 0' },
                    },
                  }}
                >
                  {/* Header placeholder */}
                  <Box sx={{
                    height: 44,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: 'action.hover', flexShrink: 0 }} />
                    <Box sx={{ height: 11, borderRadius: '4px', bgcolor: 'action.hover', width: '45%' }} />
                  </Box>
                  {/* Content placeholder */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.5, px: 2, py: 2 }}>
                    <Box sx={{ height: 10, borderRadius: '4px', bgcolor: 'action.hover', width: '70%', mx: 'auto' }} />
                    <Box sx={{ height: 10, borderRadius: '4px', bgcolor: 'action.hover', width: '50%', mx: 'auto' }} />
                    <Box sx={{ height: 10, borderRadius: '4px', bgcolor: 'action.hover', width: '60%', mx: 'auto' }} />
                  </Box>
                </Box>
              )}
              
              {/* Standard header for all widgets */}
              <Box sx={(t) => ({ display: 'flex', alignItems: 'center', px: { xs: 1, sm: 1.5 }, py: 0.75, borderBottom: `1px solid ${t.tokens.border.subtle}`, gap: 0.5, flexWrap: { xs: 'nowrap', sm: 'nowrap' }, minWidth: 0 })}>
                  <Box 
                    className="widget-drag-handle" 
                    sx={{ 
                      display: isMobile ? 'none' : 'flex',
                      alignItems: 'center', 
                      mr: 1, 
                      cursor: 'grab', 
                      '&:active': { cursor: 'grabbing' }, 
                      opacity: 0.5,
                      transition: 'opacity 0.15s ease, transform 0.15s ease',
                      '&:hover': {
                        opacity: 0.8,
                        transform: 'scale(1.1)',
                      }
                    }}
                  >
                    <IconImg src={ICONS.drag} alt="drag" />
                  </Box>
                  <Box sx={{ flex: 1, pr: 0.75, minWidth: 0 }}>
                    {inlineEditId === w.widget_id ? (
                      <input
                        autoFocus
                        value={inlineEditValue}
                        onChange={(e) => setInlineEditValue(e.target.value)}
                        onBlur={() => saveInlineTitle(w.widget_id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); saveInlineTitle(w.widget_id); }
                          if (e.key === 'Escape') { setInlineEditId(null); }
                        }}
                        style={{
                          width: '100%',
                          border: '1px solid #ccc',
                          borderRadius: 4,
                          padding: '2px 6px',
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          lineHeight: 1.3,
                          outline: 'none',
                          fontFamily: 'inherit',
                          background: 'transparent',
                          color: 'inherit',
                        }}
                      />
                    ) : (
                      <Typography 
                        variant="subtitle1" 
                        onClick={() => startInlineEdit(w)}
                        sx={{ 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          fontWeight: 600,
                          fontSize: '0.8125rem',
                          lineHeight: 1.3,
                          color: 'text.primary',
                          cursor: 'text',
                          borderBottom: '1px dashed transparent',
                          transition: 'border-color 0.15s ease',
                          '&:hover': {
                            borderBottomColor: 'text.secondary',
                          }
                        }}
                      >
                        {getWidgetDisplayTitle(w)}
                      </Typography>
                    )}
                  </Box>
                  <Box className="widget-actions" sx={{ display: 'flex', alignItems: 'center', gap: 0.375 }}>
                    <Tooltip title={refreshedAt[w.widget_id] ? `Refresh · Updated ${formatAbsolute(refreshedAt[w.widget_id])}` : 'Refresh data'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => fetchWidget(w)}
                          disabled={!!widgetLoading[w.widget_id]}
                          aria-label="Refresh widget"
                          sx={(t) => ({ 
                            p: 0.5,
                            borderRadius: `${t.ds.radius.sm}px`,
                            transition: `all ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                            '&:hover': {
                              bgcolor: t.tokens.surface.sunken,
                              transform: 'scale(1.05)',
                            },
                            '&:active': {
                              transform: 'scale(0.95)',
                            },
                            '&.Mui-disabled': {
                              opacity: 0.4,
                            }
                          })}
                        >
                          <IconImg src={ICONS.refresh} alt="refresh" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Chat with Copilot">
                      <span>
                        <IconButton
                          ref={showEditNudge && w.widget_id === widgets[0]?.widget_id ? editAgentBtnRef : undefined}
                          size="small"
                          onClick={() => {
                            const shouldOnboardSummarize = showEditNudge && w.widget_id === widgets[0]?.widget_id;
                            if (showEditNudge) {
                              setShowEditNudge(false);
                              try { localStorage.removeItem('klaris_nudge_edit_agent'); } catch { /* ignore */ }
                            }
                            const hasPayload = !!(specs[w.widget_id]);
                            if (hasPayload) {
                              setInsightsOnboardingMode(shouldOnboardSummarize);
                              setInsightsFor(w);
                              setInsightsOpen(true);
                            }
                          }}
                          disabled={!specs[w.widget_id]}
                          aria-label="Chat with Copilot"
                          sx={(t) => ({ 
                            p: 0.5,
                            borderRadius: `${t.ds.radius.sm}px`,
                            transition: `all ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                            '&:hover': {
                              bgcolor: t.tokens.surface.sunken,
                              transform: 'scale(1.05)',
                            },
                            '&:active': {
                              transform: 'scale(0.95)',
                            },
                            '&.Mui-disabled': {
                              opacity: 0.4,
                            }
                          })}
                        >
                          <AutoFixHighIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="More options">
                      <IconButton 
                        size="small" 
                        onClick={(e) => openMenu(e as any, w.widget_id)}
                        sx={(t) => ({ 
                          p: 0.5,
                          borderRadius: `${t.ds.radius.sm}px`,
                          transition: `all ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                          '&:hover': {
                            bgcolor: t.tokens.surface.sunken,
                            transform: 'scale(1.05)',
                          },
                          '&:active': {
                            transform: 'scale(0.95)',
                          }
                        })}
                      >
                        <IconImg src={ICONS.more} alt="more" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              <Divider />
              <Box sx={{ 
                flex: 1, 
                minHeight: 0, 
                p: 0, m: 0,
                position: 'relative',
                // Chart and metric widgets get a small vertical inset so
                // data labels and axis text never kiss the card edges.
                '& > .widget-chart-inset': { pt: '6px', pb: '4px' },
              }}>
                {specs[w.widget_id] ? (
                  // Conditional rendering based on widget type
                  specs[w.widget_id].type === 'metric_card' ? (
                    <Box sx={{ width: '100%', height: '100%', m: 0, p: 0 }}>
                      <MetricCard 
                        metricName={specs[w.widget_id].metric_name}
                        metricValue={specs[w.widget_id].metric_value}
                        compact={false}
                        fillContainer={true}
                        hideLabel={true}
                        transparentBg={true}
                      />
                    </Box>
                  ) : specs[w.widget_id].type === 'table_result' ? (
                    <TableResultBlock
                      tableResult={{
                        data: specs[w.widget_id].data,
                        displayHint: specs[w.widget_id].display_hint || 'table',
                        eventId: specs[w.widget_id].metadata?.event_id,
                      }}
                      threadId={specs[w.widget_id].metadata?.thread_id}
                      fillContainer={true}
                    />
                  ) : specs[w.widget_id].type === 'echarts' ? (
                    // ECharts rendering (new deterministic charts)
                    <Box className="widget-chart-inset" sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <EChartsRenderer
                        option={specs[w.widget_id].spec || specs[w.widget_id]}
                        branding={branding}
                        fillContainer={true}
                        onChartReady={(instance) => {
                          chartViewRefs.current[w.widget_id] = { current: instance };
                        }}
                      />
                    </Box>
                  ) : (
                    // Legacy Vega-Lite chart rendering
                    <Box className="widget-chart-inset" sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <ChartRenderer
                        spec={specs[w.widget_id].spec || specs[w.widget_id]}
                        onViewReady={(viewRef) => {
                          chartViewRefs.current[w.widget_id] = viewRef;
                        }}
                        chartPalette={chartPalette}
                      />
                    </Box>
                  )
                ) : widgetErrors[w.widget_id] ? (
                  <Alert 
                    severity="error" 
                    variant="outlined" 
                    sx={{ 
                      height: '100%',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {widgetErrors[w.widget_id]}
                  </Alert>
                ) : (
                  <Skeleton 
                    variant="rectangular" 
                    animation="wave" 
                    sx={(t) => ({ 
                      width: '100%', 
                      height: '100%', 
                      borderRadius: `${t.ds.radius.sm}px`,
                      bgcolor: t.tokens.surface.sunken,
                    })} 
                  />
                )}
              </Box>
              </Paper>
            </div>
          ))}
        </Grid>
      )}
      </Box>
      <OnboardingCoachmark
        open={showEditNudge}
        anchorRef={editAgentBtnRef}
        message={
          isNarrowCoach
            ? 'Tap the wand to chat with Copilot.'
            : 'Click to summarize or chat with Copilot about this widget.'
        }
        placement="top-end"
      />

      <Menu 
        anchorEl={menuAnchor} 
        open={!!menuAnchor} 
        onClose={closeMenu} 
        elevation={0}
        sx={(t) => ({
          '& .MuiPaper-root': {
            borderRadius: `${t.ds.radius.md}px`,
            minWidth: 170,
            border: `1px solid ${t.tokens.border.subtle}`,
            boxShadow: t.tokens.shadow.lg,
          },
          '& .MuiMenuItem-root': {
            py: 0.875,
            px: 1.5,
            borderRadius: `${t.ds.radius.sm}px`,
            mx: 0.5,
            my: 0.25,
            fontSize: '0.875rem',
            transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
          },
          '& .MuiListItemIcon-root': {
            minWidth: 32,
          },
        })}
      >
        {/* Full Screen and Download */}
        <MenuItem onClick={() => { if (menuForId) { openFullscreen(menuForId); closeMenu(); } }}>
          <ListItemIcon>
            <IconImg src={ICONS.fullscreen} alt="fullscreen" />
          </ListItemIcon>
          <ListItemText primary="Full Screen" />
        </MenuItem>
        {/* Download option - only show for charts, not for table_result or metric_card */}
        {menuForId && specs[menuForId] && specs[menuForId].type !== 'table_result' && specs[menuForId].type !== 'metric_card' && (
          <MenuItem 
            onClick={(e) => {
              // Show submenu for chart download options
              setSubAnchor(e.currentTarget);
            }}
          >
            <ListItemIcon><Image fontSize="small" /></ListItemIcon>
            <ListItemText primary="Download" />
            <KeyboardArrowRight fontSize="small" style={{ marginLeft: 'auto' }} />
          </MenuItem>
        )}
        
        <Divider sx={{ my: 0.375 }} />
        
        {/* Delete action */}
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'error.main' }}>
            <IconImg src={ICONS.delete} alt="delete" />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={subAnchor}
        open={!!subAnchor}
        onClose={() => setSubAnchor(null)}
        elevation={0}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ dense: true }}
        sx={(t) => ({
          '& .MuiPaper-root': {
            borderRadius: `${t.ds.radius.md}px`,
            minWidth: 150,
            border: `1px solid ${t.tokens.border.subtle}`,
            boxShadow: t.tokens.shadow.lg,
          },
          '& .MuiMenuItem-root': {
            py: 0.75,
            px: 1.5,
            borderRadius: `${t.ds.radius.sm}px`,
            mx: 0.5,
            my: 0.25,
            fontSize: '0.875rem',
            transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
          },
          '& .MuiListItemIcon-root': {
            minWidth: 32,
          },
        })}
      >
        <MenuItem onClick={async () => {
          if (menuForId) await downloadWidgetAs(menuForId, 'png');
          setSubAnchor(null); closeMenu();
        }}>
          <ListItemIcon><Image fontSize="small" /></ListItemIcon>
          <ListItemText primary="PNG" />
        </MenuItem>
        {/* JPEG and SVG only for charts. We render each as an independent
            child of the Menu (instead of grouping in a Fragment) because MUI
            Menu's child enumeration can't see through Fragments and emits a
            console warning otherwise. */}
        {menuForId && specs[menuForId]?.type !== 'metric_card' && (
          <MenuItem
            key="dl-jpeg"
            onClick={async () => {
              if (menuForId) await downloadWidgetAs(menuForId, 'jpeg');
              setSubAnchor(null); closeMenu();
            }}
          >
            <ListItemIcon><PhotoCamera fontSize="small" /></ListItemIcon>
            <ListItemText primary="JPEG" />
          </MenuItem>
        )}
        {menuForId && specs[menuForId]?.type !== 'metric_card' && (
          <MenuItem
            key="dl-svg"
            onClick={async () => {
              if (menuForId) await downloadWidgetAs(menuForId, 'svg');
              setSubAnchor(null); closeMenu();
            }}
          >
            <ListItemIcon><Code fontSize="small" /></ListItemIcon>
            <ListItemText primary="SVG" />
          </MenuItem>
        )}
      </Menu>

      <AppDialog
        open={previewOpen}
        onClose={closePreviewDialog}
        size="md"
        title="Preview"
        busy={exporting}
        dividers
        primaryAction={{ label: 'Download PDF', onClick: exportDashboardPdf }}
        secondaryAction={{ label: 'Close', color: 'inherit', onClick: closePreviewDialog }}
      >
        <Box sx={{ '& img': { maxWidth: '100%' } }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </AppDialog>

      <AppDialog
        open={!!fullscreenId}
        onClose={closeFullscreen}
        size="lg"
        title={fullscreenId ? getWidgetDisplayTitle(widgets.find(w => w.widget_id === fullscreenId)!) : 'Widget'}
        // Header download menu is rendered inline (instead of using
        // <ChartDownloadButton>) because we need to dispatch to either the
        // Vega or ECharts code path depending on widget type, and the
        // legacy ChartDownloadButton always assumes a Vega view.
        headerExtras={
          fullscreenId
            && specs[fullscreenId]
            && specs[fullscreenId].type !== 'metric_card'
            && specs[fullscreenId].type !== 'table_result'
            ? (
              <>
                <Tooltip title="Download chart">
                  <IconButton
                    size="small"
                    onClick={(e) => setFullscreenDlAnchor(e.currentTarget)}
                    sx={{ p: 0.75 }}
                  >
                    <IconImg src="/icons/download.svg" alt="download" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={fullscreenDlAnchor}
                  open={Boolean(fullscreenDlAnchor)}
                  onClose={() => setFullscreenDlAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{ sx: { mt: 1, minWidth: 180 } }}
                >
                  <MenuItem
                    key="fs-dl-png"
                    onClick={async () => {
                      if (fullscreenId) await downloadWidgetAs(fullscreenId, 'png');
                      setFullscreenDlAnchor(null);
                    }}
                  >
                    <ListItemIcon><Image fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Download as PNG" />
                  </MenuItem>
                  <MenuItem
                    key="fs-dl-jpeg"
                    onClick={async () => {
                      if (fullscreenId) await downloadWidgetAs(fullscreenId, 'jpeg');
                      setFullscreenDlAnchor(null);
                    }}
                  >
                    <ListItemIcon><PhotoCamera fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Download as JPEG" />
                  </MenuItem>
                  <MenuItem
                    key="fs-dl-svg"
                    onClick={async () => {
                      if (fullscreenId) await downloadWidgetAs(fullscreenId, 'svg');
                      setFullscreenDlAnchor(null);
                    }}
                  >
                    <ListItemIcon><Code fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Download as SVG" />
                  </MenuItem>
                </Menu>
              </>
            )
            : undefined
        }
        dividers
        contentSx={{ height: '70vh' }}
      >
          {fullscreenId && specs[fullscreenId] ? (
            <Box sx={{ width: '100%', height: '100%' }}>
              {specs[fullscreenId].type === 'metric_card' ? (
                <Box sx={{ width: '100%', height: '100%' }}>
                  <MetricCard 
                    metricName={specs[fullscreenId].metric_name}
                    metricValue={specs[fullscreenId].metric_value}
                    compact={false}
                    fillContainer={true}
                    hideLabel={true}
                  />
                </Box>
              ) : specs[fullscreenId].type === 'table_result' ? (
                <Box sx={{ width: '100%', height: '100%' }}>
                  <TableResultBlock
                    tableResult={{
                      data: specs[fullscreenId].data,
                      displayHint: specs[fullscreenId].display_hint || 'table',
                      eventId: specs[fullscreenId].metadata?.event_id,
                    }}
                    threadId={specs[fullscreenId].metadata?.thread_id}
                    fillContainer={true}
                  />
                </Box>
              ) : specs[fullscreenId].type === 'echarts' ? (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Box sx={{ height: 3, flexShrink: 0, bgcolor: 'primary.main', opacity: 0.7 }} />
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <EChartsRenderer
                      option={specs[fullscreenId].spec || specs[fullscreenId]}
                      branding={branding}
                      fillContainer={true}
                      onChartReady={(instance) => {
                        chartViewRefs.current[fullscreenId] = { current: instance };
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Box sx={{ height: 3, flexShrink: 0, bgcolor: 'primary.main', opacity: 0.7 }} />
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <ChartRenderer
                      spec={specs[fullscreenId].spec || specs[fullscreenId]}
                      onViewReady={(viewRef) => {
                        chartViewRefs.current[fullscreenId] = viewRef;
                      }}
                      chartPalette={chartPalette}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          ) : fullscreenId && widgetErrors[fullscreenId!] ? (
            <Alert severity="error">{widgetErrors[fullscreenId!]}</Alert>
          ) : (
            <Skeleton variant="rectangular" animation="wave" sx={{ width: '100%', height: '100%', borderRadius: 1 }} />
          )}
      </AppDialog>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirmDialog}
        onConfirm={confirmDelete}
        title="Delete widget"
        description="Are you sure you want to delete this widget? This action cannot be undone."
        confirmLabel="Delete"
        tone="destructive"
      />
      
      <WidgetInsightsDrawer
        open={insightsOpen}
        onClose={() => { setInsightsOpen(false); setInsightsFor(null); setInsightsOnboardingMode(false); }}
        tenantId={tenantId}
        dashboardId={String(dashboardId || '')}
        widgetId={insightsFor?.widget_id || ''}
        widgetTitle={insightsFor?.title || 'Widget'}
        workflowId={insightsFor?.workflow_id}  // Chat V2
        renderedAt={insightsFor ? (refreshedAt[insightsFor.widget_id] || (insightsFor as any)?.rendered_at || null) : null}
        onboardingMode={insightsOnboardingMode}
      />
      
      <AppDialog
        open={addWidgetOpen}
        onClose={closeAddWidgetDialog}
        size="sm"
        title="Ask a question"
        dividers
        primaryAction={{
          label: 'Open Copilot',
          onClick: () => {
            if (addWidgetQuestion.trim()) {
              navigate('/copilot', { state: { initialQuestion: addWidgetQuestion.trim() } });
              closeAddWidgetDialog();
            }
          },
          disabled: !addWidgetQuestion.trim(),
        }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: closeAddWidgetDialog }}
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          Type your question below. You&apos;ll open Copilot with it filled in — press Enter there to run it, then you can add results to this dashboard from the conversation.
        </Alert>
        <TextField
          value={addWidgetQuestion}
          onChange={(e) => setAddWidgetQuestion(e.target.value)}
          label="Your question"
          placeholder="e.g., What are the top 10 products by revenue?"
          fullWidth
          multiline
          rows={3}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && addWidgetQuestion.trim()) {
              e.preventDefault();
              navigate('/copilot', { state: { initialQuestion: addWidgetQuestion.trim() } });
              closeAddWidgetDialog();
            }
          }}
        />
      </AppDialog>
    </Box>
  );
}


